import { supabase } from "../config";

// Cache for config values
let prefixCache = null;

const DEFAULT_PREFIXES = {
  prefix_male_telugu: "శ్రీ",
  prefix_female_telugu: "శ్రీమతి",
  prefix_male_english: "Sri.",
  prefix_female_english: "Smt.",
};

export const fetchPrefixes = async () => {
  if (prefixCache) return prefixCache;

  try {
    const { data } = await supabase
      .from("kathakala_config")
      .select("config_key, config_value")
      .in("config_key", [
        "prefix_male_telugu",
        "prefix_female_telugu",
        "prefix_male_english",
        "prefix_female_english",
      ]);

    if (data && data.length > 0) {
      const map = {};
      data.forEach((r) => (map[r.config_key] = r.config_value));
      prefixCache = { ...DEFAULT_PREFIXES, ...map };
    } else {
      prefixCache = DEFAULT_PREFIXES;
    }
  } catch {
    prefixCache = DEFAULT_PREFIXES;
  }
  return prefixCache;
};

/**
 * Returns prefixed name based on gender.
 * @param {string} name - The name to prefix
 * @param {string} gender - "Male" or "Female"
 * @param {string} lang - "telugu" or "english"
 * @param {object} prefixes - The prefix config object from fetchPrefixes()
 * @returns {string} Prefixed name
 */
export const prefixedName = (name, gender, lang, prefixes) => {
  if (!name || !gender || !prefixes) return name || "";
  const key = `prefix_${gender.toLowerCase()}_${lang}`;
  const prefix = prefixes[key];
  if (!prefix) return name;
  // Avoid double-prefixing
  if (name.startsWith(prefix)) return name;
  return `${prefix} ${name}`;
};

// Synchronous honorific for display (Bhaskar's standard, 2026-10-19): శ్రీ for
// men, శ్రీమతి for women, from storytellers.gender / writers.gender ('male',
// 'Male', 'Female', …). Any stored prefix or గారు / gAru is stripped first so it
// is never doubled; no honorific when the gender is unknown or for జానపదం.
// A stored prefix is a separate word ("శ్రీ వెంపటి", "Sri. X", "Smt.X"); never
// the start of a name like శ్రీనివాస్ / శ్రీదేవి / Sridevi.
const LEAD_RE = /^\s*(?:(?:Smt|Sri|Shri|Mrs|Mr)\.\s*|(?:శ్రీమతి|శ్రీ|Smt|Sri|Shri|Mrs|Mr)\.?\s+)/i;
const TAIL_RE = /\s*(గారు|gAru|garu)\.?\s*$/i;

export const bareName = (name) => {
  let n = (name || "").trim();
  for (let prev = null; prev !== n; ) {
    prev = n;
    n = n.replace(LEAD_RE, "").replace(TAIL_RE, "").trim();
  }
  return n;
};

export const isFolkWriter = (name) => bareName(name) === "జానపదం";

export const honorific = (name, gender, lang = "telugu") => {
  const bare = bareName(name);
  if (!bare || isFolkWriter(bare)) return bare;
  const g = (gender || "").toLowerCase();
  if (g !== "male" && g !== "female") return bare;
  return `${DEFAULT_PREFIXES[`prefix_${g}_${lang}`]} ${bare}`;
};
